import React, { useEffect, useState } from 'react'
import { PersonBasicInfoBlock, PersonPositionsBlock, UrlsStack } from 'components'
import { Alert, Box, CircularProgress, Snackbar, Stack, styled } from '@mui/material'
import {
  getPersonBasicInfo, type PersonBaseBasicInformation,
  type PersonBasicInformation
} from 'services'
import { useParams } from 'react-router-dom'
import { useSessionContext } from 'session'

const CenteredCircularProgress = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
})

export const PersonUpdatePage: React.FC = () => {
  const { personId } = useParams<{ personId: string }>()
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const parsedPersonId = personId != null ? parseInt(personId, 10) : 1
  const [loading, setLoading] = useState(true)
  const [showUnsuccessAlert, setShowUnsuccessAlert] = React.useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [personBasicInfo, setPersonBasicInfo] = useState <PersonBasicInformation>({
    id: 0,
    lastName: '',
    urls: [],
    country: {
      id: 0,
      country_desc: 'global',
      country_code: '',
      states: []
    }
  })

  const updateBasicInfo = (newData: PersonBaseBasicInformation): void => {
    setPersonBasicInfo(newData as PersonBasicInformation)
  }

  useEffect(() => {
    changeModuleName('Person')
  }, [changeModuleName])

  useEffect(() => {
    changeUniqueId(`${parsedPersonId} - ${personBasicInfo.uniqueId ?? ''}`)
    document.title = `Person ${parsedPersonId} - ${personBasicInfo.uniqueId ?? ''} - ${personBasicInfo.firstName ?? ''} ${personBasicInfo.lastName ?? ''}`
  }, [personBasicInfo.uniqueId])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true)
        const [personBasicInformationResponse] = await Promise.all([getPersonBasicInfo(parsedPersonId)])
        setPersonBasicInfo(personBasicInformationResponse)
      } catch (error: any) {
        let errorMessage = 'An unexpected error occurred during the request.'
        if (error.response?.status === 404 || error.response?.status === 400) {
          errorMessage = Object.values(error.response.data.errors).join('\n')
        }
        setErrorMessage(errorMessage)
        setShowUnsuccessAlert(true)
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
  }, [personId])

  const showSuccessMessage = (message: string): void => {
    setSuccessMessage(message)
    setShowSuccessAlert(true)
  }

  function handleSnackbarClose (_event: React.SyntheticEvent | Event, reason?: string): void {
    if (reason === 'clickaway') {
      return
    }

    setShowSuccessAlert(false)
    setShowUnsuccessAlert(false)
  }

  const showErrorMessage = (message: string): void => {
    setErrorMessage(message)
    setShowUnsuccessAlert(true)
  }

  return (
    <>
      {showSuccessAlert && (
          <Snackbar open={showSuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
            <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
              {successMessage}
            </Alert>
          </Snackbar>
      )}
      {showUnsuccessAlert && (
          <Snackbar open={showUnsuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
            <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
              {errorMessage}
            </Alert>
          </Snackbar>
      )}
    {loading && <CenteredCircularProgress><CircularProgress size={100} /></CenteredCircularProgress>}
    {!loading && personBasicInfo.id !== null && (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 1
        }}
      >
        <Stack spacing={2} alignItems="center" mt={1} width="98%">
          <PersonBasicInfoBlock
            originalBasicInfo={personBasicInfo}
            loading={loading}
            updateBasicInfo={updateBasicInfo}
            allowPrefixCreation={true}
          />
          <PersonPositionsBlock personId={parsedPersonId} showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage}/>
          <UrlsStack orgId={undefined} personId={parsedPersonId}/>
        </Stack>
      </Box>
    )}
  </>
  )
}
